import { useCallback, useEffect, useState } from "react";
import { useAlert } from "react-alert";
import { FormattedMessage } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useLazyQuery, useMutation } from "@apollo/client";

import { Box, Grid, Paper } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { updateCompanyName } from "../../../../actions";

import AdminSidebar from "../../../../components/Administration/AdminSidebar";
import OrganizationBranding from "../../../../components/Administration/Organization/OrganizationDetails/OrganizationBranding";
import OrganizationConfiguration from "../../../../components/Administration/Organization/OrganizationDetails/OrganizationConfiguration";
import OrganizationHeader from "../../../../components/Administration/Organization/OrganizationDetails/OrganizationHeader";
import OrganizationInformation from "../../../../components/Administration/Organization/OrganizationDetails/OrganizationInformation";

import GET_ORGANIZATION_DETAILS from "../../../../graphql/queries/CompanyAdminContext/OrganizationDetails/getOrganizationDetails";
import UPDATE_ORGANIZATION_DETAILS from "../../../../graphql/mutations/CompanyAdminContext/OrganizationDetails/updateOrganizationDetails";

const VisibilityType = Object.freeze({
  PRIVATE: "PRIVATE",
  PUBLIC: "PUBLIC",
});

const useStyles = makeStyles(theme => ({
  root: {
    flexGrow: 1,
    padding: theme.spacing(3),
    background: "#f7f8fa",
  },
}));

const emptyOrganization = {
  // F�ljande uppgifter visas i OrganizationInformation:
  name: "",
  organizationNumber: "",
  publicCompanyId: "",  // OBS! Ska inte kunna redigeras.
  vatNumber: "",
  website: "",

  // F�ljande uppgifter kommer att visas i OrganizationInformation:
  contactPerson: "",
  email: "",
  reference: "",
  telephone: "",

  // F�ljande uppgifter visas i OrganizationConfiguration:
  active: true,  // Status
  colorTheme: "",
  visibility: VisibilityType.PUBLIC,
};

const ui = {
  pageBg: "#f7f8fa",
  panel: "#ffffff",
  railBg: "#ffffff",
  border: "#e6e9ee",
  borderStrong: "#d9dee7",
  text: "#182230",
  muted: "#667085",
  subtle: "#98a2b3",
  soft: "#f8fafc",
  successBg: "#eefaf2",
  successBorder: "#b7e4c4",
  successText: "#17733b",
};

const getEditableOrganizationSnapshot = source => {
  const organization = {
    ...emptyOrganization,
    ...source,
  };

  // Ej redigerbart:  { publicCompanyId }

  return JSON.stringify({
    name: organization.name,
    organizationNumber: organization.organizationNumber,
    vatNumber: organization.vatNumber,
    website: organization.website,

    contactPerson: organization.contactPerson,
    email: organization.email,
    telephone: organization.telephone,
    reference: organization.reference,

    active: organization.active,
    colorTheme: organization.colorTheme,
    visibility: organization.visibility,
  });
};

const OrganizationDetailsPage = () => {
  const alert = useAlert();
  const dispatch = useDispatch();
  const classes = useStyles();

  const companyId = useSelector(
    state => state.api?.companyAdmin?.company?.companyId
  );

  // `draft` represents the current editable state.
  // `organization` represents the latest confirmed server state.
  // When organization data is loaded, both should be updated with the loaded data
  // so the form starts clean and `isDirty` compares the draft against server state.
  const [draft, setDraft] = useState(() => ({ ...emptyOrganization }));
  const [organization, setOrganization] = useState(() => ({ ...emptyOrganization }));

  const [disabled, setDisabled] = useState(true);
  const [logoRefreshKey, setLogoRefreshKey] = useState(0);

  // useLazyQuery is used to have explicit control over when the query is executed.
  // The hook returns the loadOrganization function, which is called from
  // refreshOrganization() when a valid companyId is available. Unlike useQuery,
  // the query is therefore not executed automatically when the component mounts.
  const [
    loadOrganization,
    { loading: loadingOrganization }
  ] = useLazyQuery(GET_ORGANIZATION_DETAILS, {
    fetchPolicy: "no-cache",
    errorPolicy: "all",
  });

  const [
    updateOrganization,
    { loading: savingOrganization }
  ] = useMutation(UPDATE_ORGANIZATION_DETAILS);

  const busy = loadingOrganization || savingOrganization;

  const currentValue = getEditableOrganizationSnapshot(draft);
  const initialValue = getEditableOrganizationSnapshot(organization);
  const isDirty = currentValue !== initialValue;

  const updateField = (field, value) => {
    setDraft(current => ({ ...current, [field]: value }));
  };

  const handleRestore = () => {
    setDraft({ ...organization });
  };

  const refreshOrganization = useCallback(async () => {
    if (!companyId)
      return;

    setDisabled(true);

    try {
      const { data, errors } = await loadOrganization({
        variables: { companyId },
      });

      if (errors?.length) {
        console.error("** Failed to load organization details", errors);
        alert.error(<FormattedMessage id="common.genericErrorMessage" />);
        return;
      }

      const loadedOrganization =
        data?.companyAdminContext?.organizationDetails?.getOrganizationDetails;

      if (!loadedOrganization) {
        alert.error(<FormattedMessage id="common.genericErrorMessage" />);
        return;
      }

      const organizationState = {
        ...emptyOrganization,
        ...loadedOrganization,
      };

      setOrganization(organizationState);
      setDraft(organizationState);
      setDisabled(false);

    } catch (error) {
      console.error("** Failed to load organization details", error);
      alert.error(<FormattedMessage id="common.genericErrorMessage" />);
    }
  }, [alert, companyId, loadOrganization]);

  const handleSave = async () => {
    if (!companyId || !isDirty)
      return;

    setDisabled(true);

    try {
      const { data, errors } = await updateOrganization({
        variables: {
          companyId,
          details: {
            active: draft.active,
            defaultTheme: draft.colorTheme,
            name: draft.name,
            orgNo: draft.organizationNumber,
            vatNo: draft.vatNumber,
            visibility: draft.visibility,
            website: draft.website,
          },
        },
      });

      if (errors?.length) {
        console.error("** Failed to update organization details", errors);
        alert.error(<FormattedMessage id="common.genericErrorMessage" />);
        return;
      }

      const updatedOrganization =
        data?.companyAdminContext?.organizationDetails?.updateOrganizationDetails;

      if (!updatedOrganization) {
        alert.error(<FormattedMessage id="common.genericErrorMessage" />);
        return;
      }

      const organizationState = {
        ...emptyOrganization,
        ...updatedOrganization,
      };

      setOrganization(organizationState);
      setDraft(organizationState);

      dispatch(
        updateCompanyName({
          companyId: updatedOrganization.companyId,
          newName: updatedOrganization.name,
        })
      );
    } catch (error) {
      console.error("** Failed to update organization details", error);
      alert.error(<FormattedMessage id="common.genericErrorMessage" />);
    } finally {
      setDisabled(false);
    }
  };

  const handleTemplateImported = async () => {
    setLogoRefreshKey(value => value + 1);
    return refreshOrganization();
  };

  useEffect(() => {
    refreshOrganization();
  }, [refreshOrganization]);



  return (
    <Box className={classes.root}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={2}>
          <AdminSidebar />
        </Grid>

        <Grid item xs={12} md={9} lg={10} sx={{ minWidth: 0 }}>
          <OrganizationHeader
            busy={busy}
            dirty={isDirty}
            disabled={disabled}
            invalid={draft.name.trim().length === 0}
            onRestore={handleRestore}
            onSave={handleSave}
          />

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", lg: "254px minmax(0, 1fr)" },
              gap: { xs: 1.25, lg: 1.75 },
              alignItems: { xs: "start", lg: "stretch" },
            }}
          >
            <Paper
              elevation={0}
              sx={{
                border: `1px solid ${ui.border}`,
                borderRadius: 1,
                bgcolor: ui.panel,
                overflow: "hidden",
                height: { xs: "auto", lg: "100%" },
                display: "flex",
                flexDirection: "column",
                boxShadow: "0 1px 2px rgba(16,24,40,.035), 0 6px 18px rgba(16,24,40,.014)",
              }}
            >
              <OrganizationBranding
                companyId={companyId}
                disabled={disabled}
                logoRefreshKey={logoRefreshKey}
              />

              <OrganizationConfiguration
                companyId={companyId}
                disabled={disabled}
                draft={draft}
                onChange={updateField}
                onImported={handleTemplateImported}
              />
            </Paper>

            <Paper
              elevation={0}
              sx={{
                border: `1px solid ${ui.border}`,
                borderRadius: 1,
                bgcolor: ui.panel,
                overflow: "hidden",
                height: { xs: "auto", lg: "100%" },
                display: "flex",
                flexDirection: "column",
                boxShadow: "0 1px 2px rgba(16,24,40,.035), 0 6px 18px rgba(16,24,40,.014)",
              }}
            >
              <OrganizationInformation
                disabled={disabled}
                draft={draft}
                onChange={updateField}
              />
            </Paper>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

OrganizationDetailsPage.displayName = "OrganizationDetailsPage";

export default OrganizationDetailsPage;
